import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Video, Mic2, Play, Clock, Eye, Search, Filter } from 'lucide-react';
import { StageEmptyState } from '../components/StageEmptyState';
import { API } from '../config/api';



export default function InterviewsPage() {
  const [interviews, setInterviews] = useState([]);
  const [featuredInterviews, setFeaturedInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [formatFilter, setFormatFilter] = useState('all');

  useEffect(() => {
    fetchInterviews();
  }, [formatFilter]);

  const fetchInterviews = async () => {
    try {
      const params = new URLSearchParams();
      if (formatFilter !== 'all') params.append('format', formatFilter);
      
      const [interviewsRes, featuredRes] = await Promise.all([
        fetch(`${API}/interviews?${params}`),
        fetch(`${API}/interviews/featured?limit=5`)
      ]);
      
      if (interviewsRes.ok) setInterviews(await interviewsRes.json());
      if (featuredRes.ok) {
        const data = await featuredRes.json();
        setFeaturedInterviews(data.featured_interviews || []);
      }
    } catch (error) {
      console.error('Failed to fetch interviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatBadgeColor = (format) => {
    switch (format) {
      case 'video': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'audio': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'live': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-muted';
    }
  };

  const filteredInterviews = interviews.filter(interview => 
    interview.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    interview.guest_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    interview.host_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" data-testid="interviews-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold uppercase mb-2">
            <span className="text-secondary">Interviews</span>
          </h1>
          <p className="text-muted-foreground text-lg">
            Conversations with DMV artists, podcasters, and creatives
          </p>
        </div>

        {/* Featured Section */}
        {featuredInterviews.length > 0 && (
          <section className="mb-12">
            <h2 className="font-heading text-2xl font-bold uppercase mb-4 flex items-center gap-2">
              <Play className="w-5 h-5 text-secondary" />
              Featured
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {featuredInterviews.slice(0, 3).map((interview) => (
                <Link 
                  key={interview.interview_id} 
                  to={`/interviews/${interview.interview_id}`}
                  data-testid={`featured-interview-${interview.interview_id}`}
                >
                  <Card className="glass-card border-secondary/30 hover:border-secondary/60 transition-all group overflow-hidden">
                    <div className="aspect-video bg-gradient-to-br from-secondary/20 to-primary/20 relative">
                      {interview.cover_url ? (
                        <img src={interview.cover_url} alt={interview.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Video className="w-12 h-12 text-secondary/50" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Play className="w-12 h-12 text-white" />
                      </div>
                      <Badge className={`absolute top-2 right-2 ${formatBadgeColor(interview.format)}`}>
                        {interview.format}
                      </Badge>
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-lg line-clamp-1 group-hover:text-secondary transition-colors">
                        {interview.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {interview.host_name} × {interview.guest_name}
                      </p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" /> {interview.plays || 0}
                        </span>
                        {interview.duration && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {interview.duration}
                          </span>
                        )}
                      </div>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search interviews..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-muted/50 border-border"
              data-testid="interviews-search"
            />
          </div>
          <Select value={formatFilter} onValueChange={setFormatFilter}>
            <SelectTrigger className="w-[180px]" data-testid="format-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Format" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Formats</SelectItem>
              <SelectItem value="video">Video</SelectItem>
              <SelectItem value="audio">Audio Only</SelectItem>
              <SelectItem value="live">Live</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* All Interviews Grid */}
        <section>
          <h2 className="font-heading text-xl font-bold uppercase mb-4">
            All Interviews ({filteredInterviews.length})
          </h2>
          
          {filteredInterviews.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredInterviews.map((interview) => (
                <Link 
                  key={interview.interview_id} 
                  to={`/interviews/${interview.interview_id}`}
                  data-testid={`interview-card-${interview.interview_id}`}
                >
                  <Card className="glass-card border-border hover:border-border transition-all group h-full">
                    <div className="aspect-video bg-muted/30 relative">
                      {interview.cover_url ? (
                        <img src={interview.cover_url} alt={interview.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          {interview.format === 'audio' ? (
                            <Mic2 className="w-8 h-8 text-muted-foreground" />
                          ) : (
                            <Video className="w-8 h-8 text-muted-foreground" />
                          )}
                        </div>
                      )}
                      <Badge className={`absolute top-2 right-2 text-xs ${formatBadgeColor(interview.format)}`}>
                        {interview.format}
                      </Badge>
                    </div>
                    <div className="p-3">
                      <h3 className="font-medium line-clamp-2 group-hover:text-secondary transition-colors">
                        {interview.title}
                      </h3>
                      <div className="mt-2 space-y-1">
                        <p className="text-xs text-muted-foreground">
                          Host: <span className="text-foreground">{interview.host_name}</span>
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Guest: <span className="text-foreground">{interview.guest_name}</span>
                        </p>
                      </div>
                      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" /> {interview.plays || 0}
                        </span>
                        {interview.duration && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {interview.duration}
                          </span>
                        )}
                      </div>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <StageEmptyState 
              variant="interviews"
              customHint={searchQuery ? 'No interviews match your search.' : undefined}
            />
          )}
        </section>
      </div>
    </div>
  );
}
